import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Modal, Flex, List, Button, Input, Tooltip, Spin, Divider } from 'antd';
import { useNavigate } from 'react-router-dom';
import { debounce, isEmpty } from 'lodash';
import CustomAvatar from '../../../Components/CustomAvatar';
import ApiService from '../../../API/apiService';
import { API_ENDPOINTS } from '../../../API/endpoints';
import { handleApiError } from '../../../utils/CommonFunctions';

const DirectMessageModal = ({ open, onCancel, getChatListApi, getFavListApi }) => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const listItemRefs = useRef([]);

  const [searchText, setSearchText] = useState('');
  const [suggestedUsers, setSuggestedUsers] = useState([]);
  const [totalSearchUsers, setTotalSearchUsers] = useState(null);
  const [searchCounts, setSearchCounts] = useState({});
  const [userName, setUserName] = useState([]);
  const [hoverState, setHoverState] = useState('');
  const [showLoader, setShowLoader] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const UserData = JSON.parse(localStorage.getItem("userData"));
  let rout = UserData?.domain || 'econnect';

  // Calculate filtered users
  const filteredUsers = useMemo(() => {
    return searchText
      ? userName.filter(user =>
          user.userName?.toLowerCase().includes(searchText.toLowerCase()) ||
          user.fullName?.toLowerCase().includes(searchText.toLowerCase())
        )
      : suggestedUsers;
  }, [searchText, userName, suggestedUsers]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listItemRefs.current[highlightedIndex]) {
      listItemRefs.current[highlightedIndex].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [highlightedIndex]);

  useEffect(() => {
    if (open) {
      userSuggestions();
      setHighlightedIndex(-1);
    }
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const userSuggestions = async () => {
    try {
      const response = await ApiService.get(API_ENDPOINTS.getUserSuggestions);
      if (response?.data) {
        setSuggestedUsers(response.data?.suggestions);
        setTotalSearchUsers(response.data?.total_users);
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const searchUserApi = async (searchTerm) => {
    try {
      const response = await ApiService.post(
        API_ENDPOINTS.searchUserChannel,
        { searchTerm, userId: UserData?._id }
      );
      if (response?.data && response?.statusCode == 200) {
        setSearchCounts(response.data);
        setUserName(
          response.data.users.length > 0 ? response?.data?.users : []
        );
        setHighlightedIndex(-1); // Reset highlighted index on new results
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const debouncedSearchUserApi = useMemo(() => {
    return debounce((value) => {
      searchUserApi(value?.trim());
    }, 400);
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    setHighlightedIndex(-1); // Reset highlighted index on input change

    if (value !== "") {
      debouncedSearchUserApi(value);
    } else {
      userSuggestions();
    }
  };

  const handleDirectMessageSearchModalCancel = () => {
    setSearchText("");
    setHighlightedIndex(-1);
  };

  const handleUserResponse = useCallback((responseData, status) => {
    const { user } = responseData;

    if (status) {
      navigate(`/${rout}/message/${user?.data?._id}`);
    }
  }, [navigate, rout]);

  const getUserDetailsApi = useCallback(async (id, status = true) => {
    if (!id) return false;

    try {
      const response = await ApiService.get(
        `${API_ENDPOINTS.getUserDetails}/${id}`,
      );

      const { statusCode, data } = response?.data || {};

      if (statusCode === 400) {
        const defaultChannelId = JSON.parse(localStorage.getItem('defaultChannelId'));
        navigate(`/${rout}/welcome`);
        return false;
      }

      if (!isEmpty(data) && statusCode === 200) {
        handleUserResponse(data, status, id);
        return true;
      }

      return false;
    } catch (error) {
      handleApiError(error);
      return false;
    }
  }, [navigate, rout, handleUserResponse]);

  const addUserInChatApi = async (id) => {
    if (!id || !UserData?._id) {
      return;
    }

    console.log('Starting to add user:', id);
    navigate(`/${rout}/message/${id}`);

    try {
      const chatListResponse = await ApiService.get(
        API_ENDPOINTS.getChatList,
        { userId: UserData?._id }
      );

      const userExists = chatListResponse?.data?.chatList?.some(chat => chat._id === id);

      if (!userExists) {
        await ApiService.post(API_ENDPOINTS.addUserInChatList, {
          userId: UserData._id,
          selectedUserId: id,
        });
      }

      const success = await getUserDetailsApi(id);
      if (success) {
        setUserName([]);
        handleDirectMessageSearchModalCancel();
        onCancel();
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleUserSelect = async (item) => {
    const currentId = localStorage.getItem("current_id");
    if (!currentId) {
      localStorage.setItem("previous_id", "");
      localStorage.setItem("current_id", item?.userId);
    } else {
      localStorage.setItem("previous_id", currentId);
      localStorage.setItem("current_id", item?.userId);
    }

    await addUserInChatApi(item?.userId);
    onCancel();
  };

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e) => {
      const currentFilteredUsers = searchText
        ? userName.filter(user =>
            user.userName?.toLowerCase().includes(searchText.toLowerCase()) ||
            user.fullName?.toLowerCase().includes(searchText.toLowerCase())
          )
        : suggestedUsers;

      if (!currentFilteredUsers.length) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev < currentFilteredUsers.length - 1 ? prev + 1 : prev
          );
          break;

        case "ArrowUp":
          e.preventDefault();
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
          break;

        case "Enter":
          e.preventDefault();
          if (highlightedIndex >= 0 && currentFilteredUsers[highlightedIndex]) {
            handleUserSelect(currentFilteredUsers[highlightedIndex]);
          }
          break;

        case "Escape":
          e.preventDefault();
          setSearchText("");
          setHighlightedIndex(-1);
          break;

        default:
          break;
      }
    },
    [searchText, userName, suggestedUsers, highlightedIndex, handleUserSelect]
  );

  return (
    <Modal
      title="Direct Messages"
      open={open}
      onCancel={onCancel}
      footer={null}
      className="ec-modal direct-message-search-modal"
      width={600}
      style={{ top: 20 }}
      destroyOnClose
    >
      <Flex gap="small" className="ant-modal-subheader">
        <div className="search-container-wrapper">
          <div className="search-container">
            <div className="search-tags-wrapper">
              <Input
                className="search-input"
                placeholder="Search names"
                value={searchText}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                ref={inputRef}
              />
            </div>
          </div>
        </div>
      </Flex>

      <Flex justify="space-between" gap="small" className="member-details">
        <p>
          {searchText
            ? `${searchCounts.totalSearchResults || 0} of ${totalSearchUsers || 0} members`
            : `${suggestedUsers.length || 0} of ${totalSearchUsers || 0} members`}
        </p>
      </Flex>

      <Flex vertical className="suggestion-list-container">
        {showLoader ? (
          <Spin size="default" />
        ) : (
          <>
            {searchText ? (
              filteredUsers.length > 0 ? (
                <List
                  className="suggestion-list"
                  itemLayout="horizontal"
                  dataSource={filteredUsers}
                  renderItem={(item, index) => (
                    <List.Item
                      ref={(el) => (listItemRefs.current[index] = el)}
                      onMouseEnter={() => {
                        setHoverState(item?.userId);
                        setHighlightedIndex(index);
                      }}
                      className={hoverState === item?.userId ? "hoverClass" : ""}
                      onClick={() => handleUserSelect(item)}
                      style={{
                        cursor: "pointer",
                        backgroundColor:
                          highlightedIndex === index
                            ? "#f0f0f0"
                            : "transparent",
                      }}
                    >
                      <List.Item.Meta
                        avatar={
                          <CustomAvatar
                            img={item?.thumbnailAvatarUrl}
                            size={40}
                            userName={item?.fullName || item?.userName}
                          />
                        }
                        title={
                          <>
                            <span className="title">
                              {item?.userName || ""}
                            </span>
                            <span className="subtitle">
                              {item?.fullName ? ` - ${item.fullName}` : ""}
                            </span>
                            <span className="designation">
                              {item?.position ? ` - ${item.position}` : ""}
                            </span>
                          </>
                        }
                        description={item.elsner_email || item.email}
                      />
                      <Button type="text" className="add-btn">{item.content}</Button>
                    </List.Item>
                  )}
                />
              ) : (
                <p className="no-results-message">
                  No results found matching "{searchText}"
                </p>
              )
            ) : (
              <>
                {suggestedUsers.length > 0 && (
                  <List
                    className="suggestion-list"
                    itemLayout="horizontal"
                    dataSource={suggestedUsers}
                    renderItem={(item, index) => (
                      <List.Item
                        ref={(el) => (listItemRefs.current[index] = el)}
                        onMouseEnter={() => {
                          setHoverState(item.userId);
                          setHighlightedIndex(index);
                        }}
                        className={hoverState === item?.userId ? "hoverClass" : ""}
                        onClick={() => {
                          addUserInChatApi(item?.userId);
                          onCancel();
                        }}
                        style={{
                          cursor: "pointer",
                          backgroundColor:
                            highlightedIndex === index
                              ? "#f0f0f0"
                              : "transparent",
                        }}
                      >
                        <List.Item.Meta
                          avatar={
                            <CustomAvatar
                              img={item?.thumbnailAvatarUrl}
                              size={40}
                              userName={item?.fullName || item?.userName}
                            />
                          }
                          title={
                            <>
                              <span className="title">
                                {item?.userName || ""}
                              </span>
                              <span className="subtitle">
                                {item?.fullName ? ` - ${item.fullName}` : ""}
                              </span>
                              <span className="designation">
                                {item?.position ? ` - ${item.position}` : ""}
                              </span>
                            </>
                          }
                          description={item.elsner_email || item.email}
                        />
                        <Button type="text" className="add-btn">{item.content}</Button>
                      </List.Item>
                    )}
                  />
                )}
              </>
            )}
          </>
        )}
      </Flex>
    </Modal>
  );
};

export default DirectMessageModal;